import PreHeadingElement from "@/app/(site)/_components/GCMolecules/PreHeadingElement"
import SubHeadingAndButtons from "@/app/(site)/_components/GCSanityBlocks/HeadingBlock/SubHeadingAndButtons"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import HeadingGroup from "@/app/(site)/_components/SanityGroups/HeadingGroup"
import { gapClass } from "@/config/fontConfig/gapClass"
import { type TextColorType } from "@/gc-sanity-types"
import { type HeadingBlockData, type HeadingLevel } from "@/types/gc-modules-types"
import { clsx } from "@/src/utils/clsx"
import { getGeneralLinkHref } from "@/src/utils/links/getGeneralLinkHref"

type HeadingBlockProps = {
    data: HeadingBlockData
    isAboveTheFold?: boolean
    className?: string
    textColor?: TextColorType
    headingLevelOverride?: HeadingLevel
}

export default function HeadingBlock({
    data,
    isAboveTheFold,
    className,
    textColor,
    headingLevelOverride
}: HeadingBlockProps) {
    if (!data) return null

    const {
        preHeadingElement,
        headingGroup,
        subHeadingGroup,
        buttonGroup,
        direction = "vertical"
    } = data

    const blockContainsSubHeaderOrButtons =
        subHeadingGroup?.subHeadingElement?.text?.trim() || buttonGroup?.buttons

    const singleButton =
        buttonGroup?.buttons?.length === 1 ? buttonGroup.buttons[0] : null
    const singleButtonLink = singleButton?.link && getGeneralLinkHref(singleButton.link)
    const shouldWrapWithLink = !!singleButtonLink

    const wrapHeadingWithLink = (children: React.ReactNode) =>
        shouldWrapWithLink ? (
            <DynamicLink
                href={singleButtonLink!}
                prefetch={false}
                className="inline-block w-fit max-w-full"
            >
                {children}
            </DynamicLink>
        ) : (
            children
        )

    return (
        <div
            className={clsx(
                "flex w-full flex-col items-start !gap-y-0",
                "sm:grid sm:grid-cols-8 sm:gap-16",
                direction === "horizontal" && "md:grid md:grid-cols-12 md:gap-24",
                direction === "vertical" && "md:grid md:grid-cols-6 md:gap-24",
                className
            )}
        >
            {preHeadingElement?.text && (
                <div
                    className={clsx(
                        "mb-96 sm:col-span-8 md:mb-160",
                        direction === "horizontal"
                            ? "md:col-span-1 md:col-start-1 md:!mb-0"
                            : "md:col-span-6"
                    )}
                >
                    <PreHeadingElement
                        text={preHeadingElement.text}
                        className={textColor || "text-main-black"}
                    />
                </div>
            )}

            {headingGroup && (
                <div
                    className={clsx(
                        "flex flex-col justify-start",
                        "w-full max-w-[802px] sm:col-span-4",
                        blockContainsSubHeaderOrButtons &&
                            gapClass(
                                headingGroup?.headingGroupContent?.styleKey ||
                                    "Trade_Gothic_Header_32_105_0_300"
                            ),
                        "sm:mb-0",
                        direction === "horizontal" && "md:!mb-0",
                        direction === "horizontal"
                            ? preHeadingElement?.text
                                ? "md:col-span-6 md:col-start-2"
                                : "md:col-span-6 md:col-start-1"
                            : "md:col-span-6"
                    )}
                >
                    {wrapHeadingWithLink(
                        <HeadingGroup
                            data={{
                                headingGroupContent: headingGroup.headingGroupContent,
                                textColor
                            }}
                            levelOverride={headingLevelOverride}
                            isAboveTheFold={!!isAboveTheFold}
                            className="flex max-w-[802px] flex-col"
                        />
                    )}
                </div>
            )}

            {(subHeadingGroup || buttonGroup) && (
                <div
                    className={clsx(
                        "flex w-full flex-col justify-start",
                        "sm:col-span-4",
                        direction === "horizontal"
                            ? "md:col-span-5 md:col-start-8"
                            : "sm:mt-0 md:col-span-6"
                    )}
                >
                    <SubHeadingAndButtons
                        subHeadingGroup={subHeadingGroup}
                        buttonGroup={buttonGroup}
                        wrapSubHeadingWithLink={shouldWrapWithLink}
                        subHeadingLinkHref={singleButtonLink}
                        textColor={textColor || "text-main-black"}
                        className={clsx(direction === "horizontal" && "md:self-end")}
                    />
                </div>
            )}
        </div>
    )
}
